import React, { useRef, useState } from 'react';
import { Control, Controller, FieldValues, UseFormSetValue } from 'react-hook-form';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';

import { Button, Input, Select } from 'antd';

import { COUNTRY_OPTIONS, normaliseCountryValue } from 'utils/countries';
import FieldError from './FieldError';

const libraries: 'places'[] = ['places'];

export type Address = {
  streetNumber: string | null;
  streetName: string | null;
  streetType: string | null;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
  country: string | null;
  formattedAddress: string | null;
} | null;

interface AddressInputProps {
  control?: Control<any>;
  name: string;
  className?: string;
  placeholder?: string;
  defaultValue?: {
    street?: string;
    suburb?: string;
    state?: string;
    postcode?: string;
    formattedAddress?: string;
    country?: string;
  };
  defaultMode: AddressMode;
  onChange?: (value: Address) => void;
  isSelectedIndividual?: boolean;
  setValue: UseFormSetValue<FieldValues>;
}

export enum AddressMode {
  Manual = 'manual',
  Lookup = 'lookup',
}

export const AU_STATES_SHORTNAME = ['NSW', 'VIC', 'ACT', 'NT', 'QLD', 'SA', 'WA'];

const AddressField: React.FC<AddressInputProps> = ({
  setValue,
  control,
  name,
  className,
  onChange,
  placeholder = '',
  isSelectedIndividual = false,
  defaultValue,
  defaultMode,
}) => {
  const [mode, setMode] = useState<AddressMode>(defaultMode);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
    libraries,
  });

  const inputRef = useRef<google.maps.places.Autocomplete | null>(null);

  const handleOnPlaceSelected = (field: { onChange: (value: string) => void }) => {
    if (!inputRef.current) return;
    const place = inputRef.current.getPlace();
    if (!place || !place.address_components) return;

    const addressComponents = place.address_components || [];

    const getComponent = (type: string, useShortName = false): string =>
      addressComponents.find((comp) => comp.types.includes(type))?.[
        useShortName ? 'short_name' : 'long_name'
      ] || '';

    const streetName = [
      getComponent('street_number'),
      getComponent('route'),
      getComponent('street_type'),
    ].join(' ');
    const suburb = getComponent('locality');
    const state = getComponent('administrative_area_level_1', true);
    const postcode = getComponent('postal_code');
    const country = normaliseCountryValue(getComponent('country'));

    setValue(name + '.streetName', streetName);
    setValue(name + '.suburb', suburb);
    setValue(name + '.state', state);
    setValue(name + '.postcode', postcode);
    setValue(name + '.country', country);

    const parsedAddress = [streetName, suburb, state, postcode, country].filter((s) => s).join(' ');

    field.onChange(parsedAddress);
  };

  if (!isLoaded) return <p>Loading Google Maps...</p>;

  return mode === AddressMode.Lookup ? (
    <Controller
      name={name + '.address'}
      control={control}
      rules={{ required: 'Address is required' }}
      render={(ss) => {
        const {
          field,
          fieldState: { error },
        } = ss;
        return (
          <Autocomplete
            onLoad={(ref) => (inputRef.current = ref)}
            onPlaceChanged={() => handleOnPlaceSelected(field)}
            options={{ componentRestrictions: { country: 'AU' } }}
          >
            <div>
              <Input
                {...field}
                placeholder={placeholder}
                className={`mb-2 ${className} ${error ? 'border-red-500' : ''}`}
                onChange={(e) => field.onChange(e)}
              />
              <Button
                type="text"
                className="text-xs text-gray-600"
                onClick={() => setMode(AddressMode.Manual)}
              >
                Enter address manually
              </Button>
              <FieldError message={isSelectedIndividual ? error?.message : undefined} />
            </div>
          </Autocomplete>
        );
      }}
    />
  ) : (
    <div>
      <div className="flex gap-2 mb-2">
        <Controller
          defaultValue={defaultValue?.street}
          name={name + '.streetName'}
          control={control}
          rules={{ required: 'Street is required' }}
          render={({ field, fieldState: { error } }) => (
            <div className="w-full">
              <Input
                {...field}
                placeholder="Street"
                className="h-10 font-medium"
                onChange={(e) => field.onChange(e)}
              />
              <FieldError message={error?.message} />
            </div>
          )}
        />
        <Controller
          defaultValue={defaultValue?.suburb}
          name={name + '.suburb'}
          control={control}
          rules={{ required: 'Suburb is required' }}
          render={({ field, fieldState: { error } }) => (
            <div className="w-full">
              <Input
                {...field}
                placeholder="Suburb"
                className="h-10 font-medium"
                onChange={(e) => field.onChange(e)}
              />
              <FieldError message={error?.message} />
            </div>
          )}
        />
      </div>
      <div className="flex gap-2 mb-2">
        <Controller
          defaultValue={defaultValue?.state}
          name={name + '.state'}
          control={control}
          rules={{ required: 'State is required' }}
          render={({ field, fieldState: { error } }) => (
            <div className="w-full">
              <Select
                {...field}
                className="min-w-[312px] h-10 font-medium"
                options={AU_STATES_SHORTNAME.map((s) => ({
                  label: s,
                  value: s,
                }))}
                onChange={(e) => field.onChange(e)}
              />
              <FieldError message={error?.message} />
            </div>
          )}
        />
        <Controller
          defaultValue={defaultValue?.postcode}
          name={name + '.postcode'}
          control={control}
          rules={{ required: 'Postal code is required' }}
          render={({ field, fieldState: { error } }) => (
            <div className="w-full">
              <Input
                {...field}
                placeholder="Postal Code"
                className="h-10 font-medium"
                onChange={(e) => field.onChange(e)}
              />
              <FieldError message={error?.message} />
            </div>
          )}
        />
      </div>
      <div className="flex gap-2 mb-2">
        <Controller
          defaultValue={normaliseCountryValue(defaultValue?.country)}
          name={name + '.country'}
          control={control}
          rules={{ required: 'Country is required' }}
          render={({ field, fieldState: { error } }) => (
            <div className="w-full">
              <Select
                {...field}
                showSearch
                placeholder="Country"
                className="w-full h-10 font-medium"
                options={COUNTRY_OPTIONS}
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                onChange={(value) => field.onChange(value)}
              />
              <FieldError message={error?.message} />
            </div>
          )}
        />
      </div>
      <Button
        type="text"
        className="text-xs text-gray-600"
        onClick={() => setMode(AddressMode.Lookup)}
      >
        Use address lookup
      </Button>
    </div>
  );
};

export default AddressField;
